import { PackagePaymentMethod } from '@beautyapp/core';

// Per payment method: which form fields get null'd on switch, and which retain
// their value ("keep" — typically the user's bank selection). Used by the
// sell-package-modal's paymentMethod.valueChanges subscriber to prevent stale
// values from leaking across method switches (card #2892, transitively #2891
// and #2893).
//
// Must stay aligned with the backend's PAYMENT_METHOD_RELEVANT_FIELDS in
// apps/webapp-ge/src/modules/service-packages/services/payment-method-fields.ts
// (webapp-back repo). When updating one, update the other in the same PR.

export type ChannelFieldName =
  | 'cash'
  | 'card'
  | 'balance'
  | 'bankTransfer'
  | 'consignation'
  | 'bank'
  | 'bankTransferBank';

type FieldDirective = 'auto' | 'keep' | null;
// 'auto' = patch to packagePrice
// 'keep' = leave existing form value untouched
// null   = clear the field

export const PAYMENT_METHOD_RESET_TABLE: Record<
  PackagePaymentMethod,
  Record<ChannelFieldName, FieldDirective>
> = {
  CASH: {
    cash: 'auto',
    card: null,
    balance: null,
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  CARD: {
    cash: null,
    card: 'auto',
    balance: null,
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  BANK_TRANSFER: {
    cash: null,
    card: null,
    balance: null,
    bankTransfer: 'auto',
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  BALANCE: {
    cash: null,
    card: null,
    balance: 'auto',
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  BARTER: {
    cash: null,
    card: null,
    balance: null,
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  MIXED: {
    cash: null,
    card: null,
    balance: null,
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
  CONSIGNATION: {
    cash: null,
    card: null,
    balance: null,
    bankTransfer: null,
    consignation: null,
    bank: null,
    bankTransferBank: null,
  },
};

// Methods where the cashier types channel amounts. CASH/BALANCE/BARTER pay the
// full price implicitly (no amount entered), so they're exempt from the sum
// check. CARD/BANK_TRANSFER/MIXED/CONSIGNATION require the entered channels to
// sum to the package price.
export const PAYMENT_AMOUNT_METHODS: ReadonlySet<PackagePaymentMethod> =
  new Set(['CARD', 'BANK_TRANSFER', 'MIXED', 'CONSIGNATION']);

export interface PaymentChannelAmounts {
  cash?: number | null;
  card?: number | null;
  balance?: number | null;
  bankTransfer?: number | null;
  consignation?: number | null;
}

export interface PaymentSumMismatch {
  expected: number;
  entered: number;
}

/**
 * Shared payment-sum check used by BOTH the admin sell-package-modal and the
 * in-booking package-sell-dialog (beaut-219). For amount methods the entered
 * channels must sum to the package price — no over/under payment. Returns the
 * mismatch detail, or null when valid (or the method pays full price
 * implicitly). A ±0.001 tolerance absorbs decimal(2)/float tails.
 *
 * Single source of truth so the two modals can never drift — the drift between
 * them was the root cause of beaut-219 (the booking dialog skipped this check).
 */
export function paymentSumMismatch(
  method: PackagePaymentMethod,
  packagePrice: number,
  channels: PaymentChannelAmounts
): PaymentSumMismatch | null {
  if (!PAYMENT_AMOUNT_METHODS.has(method)) return null;
  const sum =
    (Number(channels.cash) || 0) +
    (Number(channels.card) || 0) +
    (Number(channels.balance) || 0) +
    (Number(channels.bankTransfer) || 0) +
    (Number(channels.consignation) || 0);
  const entered = Math.round(sum * 100) / 100;
  return Math.abs(entered - packagePrice) > 0.001
    ? { expected: packagePrice, entered }
    : null;
}

export function buildResetPatch(
  method: PackagePaymentMethod,
  packagePrice: number
): Partial<Record<ChannelFieldName, number | null>> {
  const table = PAYMENT_METHOD_RESET_TABLE[method];
  const patch: Partial<Record<ChannelFieldName, number | null>> = {};
  for (const [field, directive] of Object.entries(table) as Array<
    [ChannelFieldName, FieldDirective]
  >) {
    if (directive === 'auto') {
      patch[field] = packagePrice;
    } else if (directive === null) {
      patch[field] = null;
    }
    // 'keep' → omit from patch so existing value is retained
  }
  return patch;
}
